import { Controller } from "@hotwired/stimulus"

// Click-to-enlarge lightbox for inner-page figures (SHI-194).
// Mounted on <body> in app/views/layouts/static_home_inner.html.slim.
// Triggers are emitted by guests/static_homes/_figure_lightbox_trigger.slim
// (consumed via per-template _figure.html.slim partials).
//
// Caption HTML: captions are rendered via innerHTML, matching the page
// renderer (figcaption == caption in Slim is also raw-HTML). CKEditor
// is the sanitization boundary on the way in; the modal should look
// the same as the inline figcaption (italics, line breaks preserved).
export default class extends Controller {
  static targets = ["dialog", "image", "caption", "prev", "next"]

  triggers = []
  currentIndex = 0

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("close", () => this.cleanup())
    }
  }

  disconnect() {
    this.cleanup()
  }

  open(event) {
    // Trigger is a <button type="button"> — no preventDefault needed.
    const trigger = event.currentTarget
    this.collectTriggers()
    this.currentIndex = this.triggers.findIndex((el) => el === trigger)
    this.show(this.triggers[this.currentIndex])
  }

  show(trigger) {
    if (!this.hasDialogTarget || !this.hasImageTarget || !this.hasCaptionTarget) return

    const { figureSrc, figureAlt, figureCaption } = trigger.dataset
    this.imageTarget.src = figureSrc
    this.imageTarget.alt = figureAlt || ""

    const caption = (figureCaption || "").trim()
    this.captionTarget.innerHTML = caption
    this.captionTarget.hidden = caption.length === 0

    this.updateNav()
    if (!this.dialogTarget.open) {
      this.dialogTarget.showModal()
      document.addEventListener("keydown", this.handleKeydown)
    }
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close()
  }

  cleanup() {
    document.removeEventListener("keydown", this.handleKeydown)
    if (this.hasImageTarget) this.imageTarget.removeAttribute("src")
    if (this.hasCaptionTarget) {
      this.captionTarget.innerHTML = ""
      this.captionTarget.hidden = true
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.show(this.triggers[this.currentIndex])
    }
  }

  next() {
    if (this.currentIndex < this.triggers.length - 1) {
      this.currentIndex++
      this.show(this.triggers[this.currentIndex])
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    } else if (event.key === "ArrowLeft") {
      this.prev()
    } else if (event.key === "ArrowRight") {
      this.next()
    }
  }

  backdropClick(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  collectTriggers() {
    this.triggers = Array.from(
      this.element.querySelectorAll("[data-action*='figure-lightbox#open']")
    )
  }

  updateNav() {
    if (this.hasPrevTarget) {
      this.prevTarget.classList.toggle("invisible", this.currentIndex <= 0)
    }
    if (this.hasNextTarget) {
      this.nextTarget.classList.toggle("invisible", this.currentIndex >= this.triggers.length - 1)
    }
  }
}
